<template>
  <div class="vr-login-wrapper">
    <div class="container-fluid h-100">
      <div class="row h-100 align-items-center justify-content-center">
        <div class="col-md-6 col-lg-4">
          <div class="login-page">
            <div class="logo-container">
              <img
                v-if="$route.query.target_package"
                src="@/assets/images/logo/verolabs-logo.png"
                alt="VEROlabs Logo"
                class="login-logo"
              >
              <b-link
                v-else
                :to="{ name: 'home' }"
              >
                <img
                  src="@/assets/images/logo/verolabs-logo.png"
                  alt="VEROlabs Logo"
                  class="login-logo"
                >
              </b-link>
            </div>

            <validation-observer
              ref="loginForm"
              v-slot="{ invalid }"
            >
              <!-- autocomplete="off" -->
              <b-form
                class="auth-login-form"
                @submit.prevent="normalLogin"
              >
                <!-- <input type="text" style="display: none;" name="fake-username" autocomplete="username">
                <input type="password" style="display: none;" name="password" autocomplete="current-password"> -->
                <b-form-group class="mb-3">
                  <validation-provider
                    v-slot="{ errors }"
                    name="email"
                    vid="email"
                    rules="required|email"
                  >
                    <div class="input-container">
                      <feather-icon
                        icon="MailIcon"
                        class="input-icon"
                        size="18"
                      />
                      <b-form-input
                        id="login-email"
                        v-model="loginForm.email"
                        :state="errors.length > 0 ? false : null"
                        name="login-email"
                        placeholder="Email"
                        class="login-input"
                        @input="microsoftError = null"
                      />
                      <!-- autocomplete="off" -->
                    </div>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group>
                  <validation-provider
                    v-slot="{ errors }"
                    name="password"
                    vid="password"
                    rules="required"
                  >
                    <div class="input-container">
                      <feather-icon
                        icon="LockIcon"
                        class="input-icon"
                        size="18"
                      />
                      <b-form-input
                        id="login-password"
                        v-model="loginForm.password"
                        :state="errors.length > 0 ? false : null"
                        :type="passwordFieldType"
                        placeholder="Password"
                        class="login-input"
                      />
                      <!-- autocomplete="new-password" -->
                      <feather-icon
                        class="cursor-pointer show-pass-icon"
                        :icon="passwordToggleIcon"
                        size="18"
                        @click="togglePasswordVisibility"
                      />
                    </div>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <div class="d-flex justify-content-start mt-1">
                  <div v-if="$route.query.target_package" />
                  <b-link
                    v-else
                    :to="{ name: 'auth-forgot-password2' }"
                    class="forgot-password-link text-underline"
                    style="font-size: 10px"
                  >
                    Forgot Password?
                  </b-link>
                </div>

                <div class="pt-2">
                  <b-button
                    type="submit"
                    variant="primary"
                    :disabled="invalid || normalLoginLoading"
                    class="login-btn w-100"
                  >
                    <b-spinner
                      v-if="normalLoginLoading"
                      small
                      class="me-2"
                    />
                    <span v-else>LOGIN</span>
                  </b-button>
                </div>

                <!-- Divider -->
                <div class="divider-wrapper my-2">
                  <div class="divider-line" />
                  <span class="divider-text">or</span>
                  <div class="divider-line" />
                </div>

                <div>
                  <b-button
                    variant="light"
                    class="microsoft-btn w-100 d-flex align-items-center justify-content-center"
                    :disabled="!loginForm.email || microsoftLoginLoading"
                    @click="loginWithMicrosoft"
                  >
                    <b-spinner
                      v-if="microsoftLoginLoading"
                      small
                      class="mr-1"
                    />
                    <img
                      v-else
                      src="@/assets/images/pages/login/microsoft.svg"
                      alt="Microsoft"
                      width="20"
                      height="20"
                      class="mr-1"
                    >
                    <span>Sign in with Microsoft</span>
                  </b-button>
                  <div
                    v-if="microsoftError"
                    class="text-danger mt-1"
                    style="font-size: 14px"
                  >
                    {{ microsoftError }}
                  </div>
                </div>

                <div class="d-flex justify-content-between mt-2">
                  <div>
                    <div v-if="$route.query.target_package" />
                    <div v-else>
                      <span class="forgot-password-link">
                        Don’t have an account?
                      </span>
                      <b-link
                        :to="{ name: 'auth-register-test' }"
                        class="forgot-password-link text-underline"
                      >
                        <span class="font-weight-bold">Sign up</span>
                      </b-link>
                    </div>
                  </div>
                  <div>
                    <div v-if="$route.query.target_package" />
                    <b-link
                      v-else
                      :to="{ name: 'home' }"
                      class="forgot-password-link text-underline"
                    >
                      <!-- <i class="vs-icon feather icon-home mr-20"></i> -->
                      <span class="font-weight-bold">Home</span>
                    </b-link>
                  </div>
                </div>
              </b-form>
            </validation-observer>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BLink,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { mapGetters, mapActions } from 'vuex'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import useJwt from '@/auth/jwt/useJwt'
import { validateMicrosoftLogin } from '@/api/system/auth'

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BLink,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      required,
      email,
      experiment_id: null,
      passwordFieldType: 'password',
      loginForm: { email: '', password: '' },
      normalLoginLoading: false,
      microsoftLoginLoading: false,
      microsoftError: null,
      loginSuccess: false,
      showHelpModal: false,
    }
  },
  computed: {
    ...mapGetters('vrCode', ['load', 'error']),
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    ...mapActions('vrCode', ['login', 'clearError']),
    togglePasswordVisibility() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password'
    },
    redirectToMicrosoft() {
      window.location.href = `${
        process.env.VUE_APP_BASE_URL || ''
      }/login/microsoft`
    },
    async normalLogin() {
      if (this.$route.query.target_package) {
        localStorage.clear()
        sessionStorage.clear()
      }
      try {
        this.normalLoginLoading = true
        const loginPayload = {
          ...this.loginForm,
        }
        if (this.$route.query.target_package) {
          loginPayload.target_package = this.$route.query.target_package
        }
        const response = await this.$store
          .dispatch('users/login', {
            query: loginPayload,
          })
          .catch(error => {
            this.isLoading = false
            this.$router.replace({ query: this.$route.query })
            if (error.response.data) {
              this.$swal({
                icon: 'error',
                title: `<h4>${error.response.data.message}</h4> `,
                showConfirmButton: true,
                confirmButtonColor: '#7367f0',
                allowOutsideClick: true,
              })
            }
          })
        if (response?.user && response?.access_token) {
          const { target_package } = this.$route.query
          const { version_number } = this.$route.query
          const userToken = response.access_token

          if (target_package) {
            const token = response.access_token
            const deepLink = `${target_package}://authorized?token=${encodeURIComponent(userToken)}&version_number=${version_number}`
            const fallbackUrl = 'https://verolabsxr.com/'

            setTimeout(() => {
              const link = document.createElement('a')
              link.href = deepLink
              link.style.display = 'none'
              document.body.appendChild(link)
              link.click()
            }, 500)
          }
          await this.handleSuccessfulLogin(
            response.user,
            response.access_token,
          )
        } else {
          throw new Error('Invalid login response')
        }
      } catch (error) {
        this.handleLoginError(error)
      } finally {
        this.normalLoginLoading = false
      }
    },
    async handleMicrosoftCallback(code) {
      try {
        this.microsoftLoginLoading = true
        const response = await this.$store.dispatch(
          'users/msLoginCallback',
          code,
        )
        const token = response.access_token || response.token
        if (response && token) {
          await this.handleSuccessfulLogin(
            response,
            token,
            response.refresh_token,
          )
        } else {
          this.$router.replace('/').catch(() => {})
        }
      } catch (error) {
        console.error('Microsoft Callback Error:', error)
        this.$swal({
          icon: 'error',
          title: 'Login Failed',
          text: 'Failed to sign in with Microsoft.',
          showConfirmButton: true,
          confirmButtonColor: '#7367f0',
        })
        this.microsoftLoginLoading = false
      }
    },
    async handleSuccessfulLogin(userData, token, refreshToken) {
      this.loginSuccess = true
      localStorage.setItem('userData', JSON.stringify(userData))
      localStorage.setItem(useJwt.jwtConfig.storageTokenKeyName, token)
      if (refreshToken) {
        localStorage.setItem(
          useJwt.jwtConfig.storageRefreshTokenKeyName,
          refreshToken,
        )
      }

      if (this.$store.hasModule('auth')) {
        this.$store.commit('auth/SET_USER_DATA', userData)
        this.$store.commit('auth/SET_ACCESS_TOKEN', token)
        if (refreshToken) {
          this.$store.commit('auth/SET_REFRESH_TOKEN', refreshToken)
        }
      }
      if (userData.ability) {
        this.$ability.update(userData.ability)
      }
      const userRole = userData.role || userData.user_type || 'user'
      const homeRoute = getHomeRouteForLoggedInUser(userRole)
      this.$router.replace('/').catch(() => {})
    },
    handleLoginError(error) {
      this.loginSuccess = false
      this.normalLoginLoading = false
      const errorMessage = this.error
        || error.response?.data?.message
        || error.message
        || 'Invalid login data'
      console.error(errorMessage)
    },
    performLogoutCleanup() {
      localStorage.clear()
      sessionStorage.clear()

      const cookies = document.cookie.split(';')
      for (const cookie of cookies) {
        const eqPos = cookie.indexOf('=')
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
      }

      if (this.$store.hasModule('auth')) {
        this.$store.commit('auth/SET_USER_DATA', null)
        this.$store.commit('auth/SET_ACCESS_TOKEN', null)
      }
    },
    async loginWithMicrosoft() {
      this.microsoftLoginLoading = true

      // Validate email before opening popup
      try {
        const response = await validateMicrosoftLogin(this.loginForm.email)
        if (
          !response
          || typeof response === 'string'
          || response.success !== true
        ) {
          throw new Error(
            response?.error || response?.message || 'Validation failed',
          )
        }
      } catch (error) {
        this.microsoftLoginLoading = false
        let errorMessage = error.message || 'Validation failed'
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message
            || error.response.data.error
            || errorMessage
        }

        this.microsoftError = errorMessage
        return
      }

      const width = 500
      const height = 600
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2
      const url = `${
        process.env.VUE_APP_BASE_URL
      }/login/microsoft?email=${encodeURIComponent(this.loginForm.email)}`

      const popup = window.open(
        url,
        'microsoft_login',
        `width=${width},height=${height},top=${top},left=${left}`,
      )

      // Check if popup is closed
      const checkPopupClosed = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(checkPopupClosed)
          this.microsoftLoginLoading = false
          window.removeEventListener('message', this.onPopupMessage)
        }
      }, 500)

      window.addEventListener('message', this.onPopupMessage)
    },
    onPopupMessage(event) {
      // Allow messages from backend if it redirects to a frontend page
      if (event.data.access_token && event.data.user) {
        this.handleSuccessfulLogin(
          event.data.user,
          event.data.access_token,
          event.data.refresh_token,
        )
        window.removeEventListener('message', this.onPopupMessage)
      } else if (event.data.code) {
        this.handleMicrosoftCallback(event.data.code)
        window.removeEventListener('message', this.onPopupMessage)
        this.$swal({
          icon: 'error',
          title: 'Login Failed',
          text: 'Failed to sign in with Microsoft.',
          showConfirmButton: true,
          confirmButtonColor: '#7367f0',
        })
        this.microsoftLoginLoading = false
      }
    },
    disableAutofill() {
      this.$nextTick(() => {
        const emailInput = document.getElementById('login-email')
        const passwordInput = document.getElementById('login-password')

        if (emailInput) {
          emailInput.setAttribute('autocomplete', 'off')
          emailInput.setAttribute('autocorrect', 'off')
          emailInput.setAttribute('autocapitalize', 'off')
          emailInput.setAttribute('spellcheck', 'false')
          emailInput.setAttribute('name', `email_${Date.now()}`)
        }

        if (passwordInput) {
          passwordInput.setAttribute('autocomplete', 'new-password')
          passwordInput.setAttribute('autocorrect', 'off')
          passwordInput.setAttribute('autocapitalize', 'off')
          passwordInput.setAttribute('spellcheck', 'false')
          passwordInput.setAttribute('name', `pass_${Date.now()}`)
        }

        setTimeout(() => {
          if (emailInput) emailInput.setAttribute('readonly', true)
          if (passwordInput) passwordInput.setAttribute('readonly', true)

          const removeReadOnly = () => {
            if (emailInput) emailInput.removeAttribute('readonly')
            if (passwordInput) passwordInput.removeAttribute('readonly')
          }

          if (emailInput) emailInput.addEventListener('focus', removeReadOnly)
          if (passwordInput) passwordInput.addEventListener('focus', removeReadOnly)
        }, 100)
      })
    },
  },
  created() {
    this.performLogoutCleanup()
    // this.disableAutofill();

    // Check for error parameter from Microsoft redirect
    if (this.$route.query.error) {
      this.$swal({
        icon: 'error',
        title: 'Authentication Error',
        text: decodeURIComponent(this.$route.query.error),
        showConfirmButton: true,
        confirmButtonColor: '#7367f0',
      })
      // Clean up the URL
      this.$router.replace({ query: {} }).catch(() => {})
      return
    }

    if (this.$route.query.code) {
      if (window.opener) {
        window.opener.postMessage(
          { code: this.$route.query.code },
          window.location.origin,
        )
        window.close()
        return
      }
      this.handleMicrosoftCallback(this.$route.query.code)
    } else if (this.$route.query.access_token && this.$route.query.user) {
      let { user } = this.$route.query
      if (typeof user === 'string') {
        try {
          user = JSON.parse(user)
        } catch (e) {
          console.error('Failed to parse user JSON', e)
        }
      }
      if (window.opener) {
        window.opener.postMessage(
          {
            access_token: this.$route.query.access_token,
            refresh_token: this.$route.query.refresh_token,
            user,
          },
          window.location.origin,
        )
        window.close()
        return
      }
      this.handleSuccessfulLogin(
        user,
        this.$route.query.access_token,
        this.$route.query.refresh_token,
      )
    }
  },
}
</script>

<style lang="scss" scoped>
.login-page {
  background: transparent;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.logo-container {
  text-align: center;
  margin-bottom: 30px;
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 15px;
  color: rgba(255, 255, 255, 0.8);
}

.login-input {
  height: 50px;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 12px;
  color: #ffffff !important;
  padding: 0 15px 0 40px;
  width: 100%;
  transition: 0.3s ease;
}

.show-pass-icon {
  position: absolute;
  right: 15px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
}

.login-btn {
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(90deg, #6c3adf 0%, #23c7ec 100%) !important;
  border: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
}

.forgot-password-link {
  color: #fff;
  font-size: 13px;
}

.microsoft-btn {
  height: 50px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-weight: 600;
  font-size: 16px;
  color: #ffffff !important;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.microsoft-btn:hover {
  background: rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 4px 15px rgba(108, 58, 223, 0.3);
  border-color: rgba(255, 255, 255, 0.4);
}

.microsoft-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.divider-wrapper {
  display: flex;
  align-items: center;
  gap: 15px;
}

.divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
}

.divider-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  font-weight: 500;
  text-transform: lowercase;
}

input:-webkit-autofill {
  background-color: rgba(255, 255, 255, 0.08) !important;
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.08) inset !important;
  -webkit-text-fill-color: white !important;
  transition: background-color 5000s ease-in-out 0s;
  caret-color: white;
}
</style>

<style lang="scss" scoped>
/* Prevent autofill styling */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  background-color: rgba(255, 255, 255, 0.08) !important;
  -webkit-box-shadow: 0 0 0px 1000px rgba(255, 255, 255, 0.08) inset !important;
  -webkit-text-fill-color: white !important;
  transition: background-color 5000s ease-in-out 0s;
  caret-color: white;
}

/* Hide autofill dropdown */
input::-webkit-contacts-auto-fill-button,
input::-webkit-credentials-auto-fill-button {
  visibility: hidden;
  display: none !important;
  pointer-events: none;
  position: absolute;
  right: 0;
}

/* Hide the autofill suggestion box */
div[role="listbox"]:has(> *[data-autofill]),
ul[role="listbox"]:has(> *[data-autofill]) {
  display: none !important;
}

.login-input {
  /* Add these additional styles */
  background-clip: padding-box !important;
  -webkit-background-clip: padding-box !important;
}
</style>
